import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { platformConfig } from '@pok/weblib';

import {
    certificateDetails,
    emptyPage,
    fetchDomainDetails,
    findPage,
    gatewayUrl,
    getDecryptedAsset,
    links,
    loadFromImageStore,
    polygonWalletLink,
    proofs as proofsLib,
    proofsOf,
    templateFromIpfs,
} from '../interact.jsx';

import { Trans, useTranslation } from 'react-i18next';

import Paper from '@mui/material/Paper';
import AdditionalInformationPreview from '@pok/weblib/ui/AdditionalInformationPreview.jsx';
import CertificateSkeleton from '../components/CertificateSkeleton.jsx';
import CertificateValidations from '../components/CertificateValidations.jsx';
import EdciDownload from '../components/EdciDownload.jsx';
import Faqs from '../components/Faqs';
import CustomSnackbar from '../components/ui/CustomSnackbar.jsx';
import ExplanationContainer from '../components/ui/ExplanationContainer.jsx';
import LinkedinShareDialog from '../components/ui/LinkedinShareDialog.jsx';
import { useLinkedinCredential } from '../utils/queries.js';
import {
    getExecuteAttemptToAddToLinkedinProfile,
    getValidatedCertificateAccessToken,
    removeValidatedCertificateAccessToken,
} from '../utils/sessionStorage.js';
import BannerImage from '../components/BannerImage.jsx';
import WidgetsContainer from '../components/WidgetsContainer.jsx';
import { trackUniqueActivity } from '../utils/custom-track-events.js';
import {
    LegacyCertificateTrackAction as CertificateTrackAction,
    LegacyStateType as StateType,
    legacySupportsEdci,
    legacyTemplateManagerAchievementNameSource as ACHIEVEMENT_NAME_SOURCE,
    legacyTemplateManagerOpenBadgeCompliant as OB_COMPLIANT,
    legacyTemplateManagerShouldBeBranded as shouldBeBranded,
    LegacyTrackCategory as TrackCategory,
} from '@pok/viewer-api-sdk';
import CertificateShare from '../components/CertificateShare.jsx';
import MinterProofs from '../components/MinterProofs.jsx';
import BlockchainLinks from '../components/ui/BlockchainLinks.jsx';
import LearningRoutesContainer from '../components/LearningRoutesContainer.jsx';
import CertificateChangeData from '../components/CertificateChangeData.jsx';
import BrandedCertificateImage from '@pok/weblib/ui/BrandedCertificateImage';
import NotFound from '../components/NotFound.jsx';
import Box from '@mui/material/Box';
import LeadDialog from '../components/ui/LeadDialog.jsx';
import { PageTrackAction } from '../../../lib/activity-metric-sdk/build/index.js';
import RelatedCredentials from '../components/RelatedCredentials.jsx';
import { customizeDomain } from '../utils/custom-domains.js';
import ProofList from '@pok/weblib/ProofsList.jsx';
import LinkedinProfileDialog from '../components/ui/LinkedinProfileDialog.jsx';

export function Certificate() {
    const { certificateId } = useParams();
    const [queryStringParams] = useSearchParams();
    const [leadDialog, setLeadDialog] = useState({ open: false, widget: undefined });
    const [notFoundCertificate, setNotFoundCertificate] = useState(false);
    const smallScreen = window.innerWidth <= 768;
    const [certificate, setCertificate] = useState(null);
    const [template, setTemplate] = useState(null);
    const [previewImg, setPreviewImg] = useState(null);
    const [openSea, setOpenSea] = useState(null);
    const [polygonScan, setPolygonScan] = useState(null);
    const [pageStyle, setPageStyle] = useState(emptyPage);
    const [validProofs, setValidProofs] = useState(true);
    const [proofsLoaded, setProofsLoaded] = useState(null);
    const [proofs, setProofs] = useState('');
    const { t } = useTranslation();
    const [walletLink, setWalletLink] = useState(null);
    const id = queryStringParams.get('state') ? queryStringParams.get('state') : certificateId;
    const [validated, setValidated] = useState(getValidatedCertificateAccessToken(id) === null ? null : true);
    const [snackOpen, setSnackOpen] = useState({
        open: false,
        textKey: '',
        messageKey: '',
    });
    const handleSnackClose = () => setSnackOpen(false);
    const [linkedinShare, setLinkedinShare] = useState({
        open: false,
        image: null,
    });
    const [tabIndex, setTabIndex] = useState(0);
    const [hasEdciView, setHasEdciView] = useState(false);
    const [name, setName] = useState('');
    const [brandType, setBrandType] = useState(undefined);
    const [assets, setAssets] = useState([]);
    const [askShareOnLinkedinProfileDialog, setAskShareOnLinkedinProfileDialog] = useState(
        getExecuteAttemptToAddToLinkedinProfile(id) !== null && getExecuteAttemptToAddToLinkedinProfile(id) === 'false',
    );

    const { data } = queryStringParams.get('code')
        ? useLinkedinCredential(
              queryStringParams.get('code'),
              () => {
                  return loadFromImageStore(id, getValidatedCertificateAccessToken(id)).then((imageUrl) => {
                      return setLinkedinShare({
                          open: imageUrl !== null && imageUrl !== undefined,
                          image: imageUrl,
                      });
                  });
              },
              () => {
                  return setSnackOpen({
                      open: true,
                      textKey: 'certificate.share.linkedin.authorization-expired',
                      messageKey: 'certificate.share.linkedin.authorization-expired',
                  });
              },
          )
        : {};

    const onProofToExternalLinkClicked = (proof, valid, location) => {
        platformConfig[proof.platform]?.onProofExternalClick(window, proof, valid, location);
    };

    const onValidationGranted = (decryptedImage, certificate) => {
        setValidated(true);
        setPreviewImg(decryptedImage);

        Promise.all(
            certificate.assets.map((a) =>
                getDecryptedAsset(certificate.id, a.id, getValidatedCertificateAccessToken(certificate.id))
                    .then((buffer) => new Blob([buffer], { type: 'application/octet-stream' }))
                    .then((file) => ({
                        ...a,
                        fileSource: file,
                    })),
            ),
        ).then((expanded) => {
            setAssets(expanded);
        });
    };

    const onValidationFailed = () => {
        setValidated(false);
    };

    const onValidationReset = (newName) => {
        setValidated(null);
        setName(newName ? newName : '');
    };

    const onRemoveValidation = (certificateId, encryptedImage) => {
        setLinkedinShare({
            open: false,
            image: null,
        });
        onValidationReset();
        removeValidatedCertificateAccessToken(certificateId);
        setPreviewImg(imageFromGateway(encryptedImage));
        setSnackOpen({
            open: true,
            textKey: 'certificate.validate_accessToken_expired',
            messageKey: 'certificate.validate_accessToken_expired',
        });
    };

    const onMainFactorAuthenticated = async (id, accessToken, certificate) => {
        loadFromImageStore(id, accessToken).then((imageUrl) => {
            if (imageUrl) {
                onValidationGranted(imageUrl, certificate);
            } else {
                onRemoveValidation(id, certificate.image);
            }
        });
    };

    const onMainFactorNotAuthenticated = (certificate) => {
        setPreviewImg(imageFromGateway(certificate.image));
        setAssets(certificate.assets);
    };

    useEffect(() => {
        legacySupportsEdci(import.meta.env.VITE_VIEWER_API_ROOT, id).then((result) => setHasEdciView(result));
    }, []);

    useEffect(() => {
        if (pageStyle.header?.color) {
            document.getElementById('pok-toolbar').style.backgroundColor = pageStyle.header.color;
            const langMenu = document.getElementById('lang-menu');
            if (langMenu) {
                langMenu.style.background = 'rgba(187, 177, 207, 0.7)';
                langMenu.style.border = '1px solid rgba(255, 255, 255, 0.2)';
                langMenu.style.borderRadius = '8px';
            }
        }
        if (pageStyle.header?.logo) {
            document.getElementById('pok-toolbar-img').src = pageStyle.header.logo;
        }
    }, [pageStyle]);

    useEffect(() => {
        certificateDetails(id)
            .then(async (c) => {
                if (c.currentState === StateType.REPLACED) window.location.href = c.replacedById;
                findPage(c.minter, c.customPage)
                    .then(setPageStyle)
                    .finally(() => setCertificate(c));
                templateFromIpfs(c.snapshot).then((definition) => {
                    setBrandType(shouldBeBranded(c.free, definition) ? c.templateType : undefined);
                    setTemplate(definition);
                });
                Promise.resolve(getValidatedCertificateAccessToken(id)).then((accessToken) => {
                    return accessToken
                        ? onMainFactorAuthenticated(id, accessToken, c)
                        : onMainFactorNotAuthenticated(c);
                });
                fetchDomainDetails(c.minter).then((cd) => customizeDomain(cd));
                return links().then((l) => {
                    setWalletLink(polygonWalletLink(l.chainId, c.minter));
                    if (c.tokenId !== undefined) {
                        l.openSea(c.tokenId).then((link) => setOpenSea(link));
                        l.polygonScan(c.tokenId).then((link) => setPolygonScan(link));
                    }

                    proofsLib()
                        .then((p) => (
                            <ProofList
                                address={{ current: c.minter }}
                                onClick={{
                                    primary: onProofToExternalLinkClicked,
                                    secondary: onProofToExternalLinkClicked,
                                }}
                                proofLoader={() => proofsOf(c.minter)}
                                library={p}
                                showInvalid={true}
                                proofsLoaded={(proofs) => handleProofs(proofs)}
                                onInvalid={() => handleInvalidProofs()}
                                role={''}
                            />
                        ))
                        .then((proofs) => setProofs(proofs))
                        .then(() =>
                            trackUniqueActivity(
                                TrackCategory.CERTIFICATE,
                                CertificateTrackAction.VIEW,
                                c.id,
                                c.minter,
                                c.customPage,
                            ),
                        );
                });
            })
            .catch(() => {
                setNotFoundCertificate(true);
            });
    }, []);

    const hasAdditionalInformation =
        (template?.info || [])
            .filter((v) => v.key !== OB_COMPLIANT && v.key !== ACHIEVEMENT_NAME_SOURCE)
            .some((item) => {
                if (Array.isArray(item.value)) {
                    return item.value.length > 0;
                } else {
                    return item.value !== '';
                }
            }) || assets.length > 0;

    const handleInvalidProofs = () => {
        setValidProofs(false);
    };
    const handleProofs = (proofs) => {
        setProofsLoaded(proofs);
    };

    const onLinkedinShareClose = () => {
        setLinkedinShare({
            open: false,
            image: null,
        });
        setAskShareOnLinkedinProfileDialog(
            getExecuteAttemptToAddToLinkedinProfile(id) !== null &&
                getExecuteAttemptToAddToLinkedinProfile(id) === 'false',
        );
    };

    const onAskShareLinkedinProfileClose = () => {
        setAskShareOnLinkedinProfileDialog(false);
    };

    const handleWidgetClick = (widget) => {
        if (widget.isLead) {
            trackAdPageClick(widget);
            setLeadDialog({ open: true, widget });
        } else if (widget?.payload?.type === 'video') {
            trackAdPageClick(widget);
        } else {
            trackAdPageClick(widget);
            if (widget.payload.link) window.open(widget.payload.link.url, '_blank');
        }
    };

    const trackAdPageClick = (widget) => {
        trackUniqueActivity(
            TrackCategory.PAGE,
            PageTrackAction.AD_CLICK,
            certificateId,
            certificate.minter,
            certificate.customPage,
            {
                x: widget.xPosition,
                y: widget.yPosition,
                version: widget.version,
            },
        );
    };
    return (
        <>
            {notFoundCertificate && <NotFound />}
            {!notFoundCertificate && (
                <>
                    <BannerImage banner={pageStyle.banner} />
                    <Container fixed>
                        {certificate && (
                            <>
                                {pageStyle.widgets?.length > 0 && (
                                    <Box
                                        sx={{
                                            zIndex: 10,
                                            marginTop: pageStyle.banner?.image ? '-100px' : '40px',
                                            display: { xs: 'none', md: 'block' },
                                        }}
                                    >
                                        <WidgetsContainer
                                            widgets={pageStyle.widgets}
                                            yPosition={'top'}
                                            color={pageStyle.style.color}
                                            textButtonColor={pageStyle.style.textButtonColor ?? '#FFFFFF'}
                                            onClick={handleWidgetClick}
                                        />
                                    </Box>
                                )}
                                <Stack mt={5} pt={{ md: 2, xs: 4 }}>
                                    <Stack
                                        boxShadow={2}
                                        borderRadius={4}
                                        px={2}
                                        mb={1}
                                        py={2}
                                        sx={{ background: '#FFF' }}
                                    >
                                        <Typography variant="h2" alignSelf={'center'}>
                                            {t('certificate.title')}
                                        </Typography>
                                        {hasAdditionalInformation && (
                                            <Tabs
                                                sx={{
                                                    mt: 1,
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    mb: 2,
                                                    alignSelf: 'center',
                                                }}
                                                value={tabIndex}
                                                onChange={(_, value) => setTabIndex(value)}
                                                textColor="inherit"
                                                TabIndicatorProps={{
                                                    sx: {
                                                        background: pageStyle.style.tabColor ?? pageStyle.style.color,
                                                    },
                                                }}
                                            >
                                                <Tab
                                                    label={t('certificate.certificate')}
                                                    sx={{ textTransform: 'none' }}
                                                ></Tab>
                                                <Tab
                                                    label={t('certificate.additional_information')}
                                                    sx={{ textTransform: 'none' }}
                                                />
                                            </Tabs>
                                        )}
                                        {tabIndex === 0 && (
                                            <>
                                                <Grid container spacing={2} mt={2} mb={1}>
                                                    <Grid size={12}>
                                                        <ExplanationContainer
                                                            titleI18n="certificate.explanation.title"
                                                            subtitleI18n="certificate.explanation.subtitle"
                                                        />
                                                    </Grid>
                                                    <Grid size={{ md: 12, lg: 8, xs: 12 }}>
                                                        <Stack spacing={2}>
                                                            <BrandedCertificateImage
                                                                src={previewImg}
                                                                footerText={
                                                                    brandType ? (
                                                                        <Trans
                                                                            i18nKey={`branding.${brandType}.footer`}
                                                                            components={[<strong />]}
                                                                        />
                                                                    ) : undefined
                                                                }
                                                                className=""
                                                            />
                                                            {previewImg === null && (
                                                                <CircularProgress
                                                                    style={{
                                                                        top: '50%',
                                                                        position: 'absolute',
                                                                        width: '60px',
                                                                        height: '60px',
                                                                    }}
                                                                />
                                                            )}

                                                            {!smallScreen && (
                                                                <>
                                                                    <RelatedCredentials
                                                                        certificate={certificate}
                                                                        buttonColor={pageStyle.style.color ?? '#5124A6'}
                                                                        textButtonColor={
                                                                            pageStyle.style.textButtonColor ?? '#FFFFFF'
                                                                        }
                                                                    />
                                                                    <MinterProofs
                                                                        validProofs={validProofs}
                                                                        proofs={proofs}
                                                                        walletLink={walletLink}
                                                                        address={certificate.minter}
                                                                        proofsLoaded={proofsLoaded}
                                                                    />
                                                                    <BlockchainLinks
                                                                        polygonScan={polygonScan}
                                                                        openSea={openSea}
                                                                    />
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </Grid>
                                                    <Grid size={{ md: 12, xs: 12, lg: 4 }}>
                                                        <Stack spacing={2}>
                                                            <CertificateValidations
                                                                styleColor={pageStyle.style.color ?? '#5124A6'}
                                                                textButtonColor={
                                                                    pageStyle.style.textButtonColor ?? '#FFFFFF'
                                                                }
                                                                onValidated={onValidationGranted}
                                                                onValidationFailed={onValidationFailed}
                                                                onValidationReset={onValidationReset}
                                                                certificate={certificate}
                                                                validated={validated}
                                                                previewImg={previewImg}
                                                                name={name}
                                                                onExpiredToken={onRemoveValidation}
                                                            />

                                                            {hasEdciView && (
                                                                <Stack>
                                                                    <EdciDownload
                                                                        certificate={certificate}
                                                                        enabled={validated}
                                                                        onExpiredToken={onRemoveValidation}
                                                                    />
                                                                </Stack>
                                                            )}
                                                            <CertificateShare
                                                                certificate={certificate}
                                                                validated={validated}
                                                            />
                                                            <LearningRoutesContainer certificate={certificate} />
                                                            <CertificateChangeData certificate={certificate} />
                                                            {smallScreen && (
                                                                <>
                                                                    <RelatedCredentials
                                                                        certificate={certificate}
                                                                        buttonColor={pageStyle.style.color ?? '#5124A6'}
                                                                        textButtonColor={
                                                                            pageStyle.style.textButtonColor ?? '#FFFFFF'
                                                                        }
                                                                    />
                                                                    <MinterProofs
                                                                        validProofs={validProofs}
                                                                        proofs={proofs}
                                                                        walletLink={walletLink}
                                                                        address={certificate.minter}
                                                                        proofsLoaded={proofsLoaded}
                                                                    />
                                                                    <BlockchainLinks
                                                                        polygonScan={polygonScan}
                                                                        openSea={openSea}
                                                                    />
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </>
                                        )}
                                        {tabIndex === 1 && hasAdditionalInformation && (
                                            <Paper
                                                elevation={2}
                                                sx={{
                                                    borderRadius: 4,
                                                    boxShadow: 1,
                                                    padding: 2,
                                                    mb: 1,
                                                    px: 4,
                                                }}
                                            >
                                                <AdditionalInformationPreview
                                                    t={t}
                                                    template={template}
                                                    gateway={import.meta.env.VITE_IPFS_GATEWAY}
                                                    validated={validated}
                                                    assets={assets}
                                                    goToValidateCallback={() => setTabIndex(0)}
                                                />
                                            </Paper>
                                        )}
                                    </Stack>
                                    <br />
                                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                        <WidgetsContainer
                                            widgets={pageStyle.widgets}
                                            yPosition={'top'}
                                            color={pageStyle.style.color}
                                            textButtonColor={pageStyle.style.textButtonColor ?? '#FFFFFF'}
                                            onClick={handleWidgetClick}
                                        />
                                    </Box>
                                    <WidgetsContainer
                                        widgets={pageStyle.widgets}
                                        yPosition={'bottom'}
                                        color={pageStyle.style.color}
                                        textButtonColor={pageStyle.style.textButtonColor ?? '#FFFFFF'}
                                        onClick={handleWidgetClick}
                                    />
                                    <Faqs></Faqs>
                                </Stack>
                                <LinkedinShareDialog
                                    open={linkedinShare.open}
                                    data={data}
                                    image={linkedinShare.image}
                                    onClose={onLinkedinShareClose}
                                    certificate={certificate}
                                />
                                <LinkedinProfileDialog
                                    open={askShareOnLinkedinProfileDialog}
                                    onClose={onAskShareLinkedinProfileClose}
                                    certificate={certificate}
                                />
                                {leadDialog.open && (
                                    <LeadDialog
                                        open={leadDialog.open}
                                        certificate={certificate}
                                        widget={leadDialog.widget}
                                        onClose={() => setLeadDialog({ open: false, widget: undefined })}
                                    />
                                )}
                                <CustomSnackbar
                                    text={t(snackOpen.textKey)}
                                    onClose={handleSnackClose}
                                    message={t(snackOpen.messageKey)}
                                    open={snackOpen.open}
                                />
                            </>
                        )}
                        {certificate === null ? <CertificateSkeleton /> : ''}
                    </Container>
                </>
            )}
        </>
    );
}

function imageFromGateway(img) {
    return img.toString().startsWith('ipfs://') ? gatewayUrl(img) : img;
}
